import { createRoot, hydrateRoot } from "react-dom/client";
import "@fontsource-variable/dm-sans";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "@fontsource/ibm-plex-mono/600.css";
import "@fontsource/stix-two-text/400.css";
import "@fontsource/stix-two-text/400-italic.css";
import "@fontsource/stix-two-text/500.css";
import "@fontsource/stix-two-text/500-italic.css";
import "@/styles/fonts.css";
import "@/styles/application.css";
import { isSimulatorEditorPath } from "@/lib/simulator-routes";
import { initKernel } from "@/lib/strategy/kernel-boot";
import { App } from "@/App";

function currentPathNeedsKernel(pathname: string): boolean {
  return (
    isSimulatorEditorPath(pathname) ||
    pathname.startsWith("/manifold") ||
    pathname.startsWith("/workspace/workbench")
  );
}

if (window.location.pathname === "/backtests") {
  const url = new URL(window.location.href);
  const template = url.searchParams.get("template");
  if (
    template?.startsWith("backtest-run:") ||
    template?.startsWith("detect:")
  ) {
    url.searchParams.delete("template");
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
  }
}

// Boot the WASM strategy kernel before render. The simulator's
// computed-structure hook calls engine.computeLayer on every render;
// without an initialized kernel the FFI throws synchronously.
const container = document.getElementById("root");
if (container) {
  const prerendered = container.firstChild !== null;
  const root = prerendered ? null : createRoot(container);
  const renderApp = () =>
    root ? root.render(<App />) : hydrateRoot(container, <App />);

  if (currentPathNeedsKernel(window.location.pathname)) {
    void initKernel()
      .catch((error) => {
        console.error("Kernel boot failed", error);
      })
      .then(() => {
        renderApp();
      });
  } else {
    renderApp();
    void initKernel().catch((error) => {
      console.error("Kernel boot failed", error);
    });
  }
}

// Dev safeguard: if a previously installed production service worker is still
// controlling localhost, it can keep serving stale shells/assets that no
// longer match the current Vite source. Clear it eagerly so local UI work
// always reflects the active code in this worktree.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      void registration.unregister();
    });
  });

  if ("caches" in window) {
    void caches.keys().then((keys) => {
      keys.forEach((key) => {
        void caches.delete(key);
      });
    });
  }
}

// Register the service worker for offline shell + Web Push delivery.
// Production-only: the dev server hot-reloads from /vite-dev/ and an
// active SW would race the HMR transport. usePushSubscription also
// calls register() on subscribe; both share the same registration
// since the script URL is identical.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker", { scope: "/" })
      .catch(() => {
        // Registration failure is non-fatal; the app degrades to
        // online-only without push delivery.
      });
  });
}
